/**
 * Shopify Dynamic Gift Card Threshold System
 * Automatically adds the appropriate gift card based on cart total
 * Only ONE gift card is added (highest tier qualified)
 */

(function() {
  'use strict';

  // Gift Card Configuration
  const GIFT_CARD_CONFIG = {
    tiers: [
      { threshold: 249, variantId: '51208847950040', value: 50, title: 'Gift Card - £50' },
      { threshold: 149, variantId: '51208847851736', value: 25, title: 'Gift Card - £25' },
      { threshold: 74, variantId: '51208847753432', value: 10, title: 'Gift Card - £10' }
    ],
      allVariantIds: ['51208847753432', '51208847851736', '51208847950040']
  };

  /**
   * Get current cart data from Shopify
   */
  async function getCart() {
    try {
      isInternalFetch = true;
      const response = await fetch('/cart.js');
      const data = await response.json();
      isInternalFetch = false;
      return data;
    } catch (error) {
      isInternalFetch = false;
      return null;
    }
  }

  /**
   * Get exchange rate from presentment currency to shop currency (GBP)
   * Returns 1 if already in GBP or if conversion not available
   */
  function getExchangeRateToGBP(presentmentCurrency) {
    // If already in GBP, no conversion needed
    if (presentmentCurrency === 'GBP') {
      return 1;
    }

    // Try to get exchange rate from Shopify.currency object
    if (typeof Shopify !== 'undefined' && Shopify.currency) {
      // Check if active currency matches
      if (Shopify.currency.active === presentmentCurrency) {
        // Get the rate - Shopify.currency.rate is conversion FROM shop currency
        // So we need to divide by it to get back to shop currency
        if (Shopify.currency.rate) {
          const rate = 1 / parseFloat(Shopify.currency.rate);
          return rate;
        }
      }
    }

    // Fallback: Try window.Currency if using a currency converter app
    if (typeof Currency !== 'undefined' && Currency.rates) {
      const gbpRate = Currency.rates.GBP;
      const presentRate = Currency.rates[presentmentCurrency];
      if (gbpRate && presentRate) {
        const rate = gbpRate / presentRate;
        return rate;
      }
    }

    return 1; // Fallback - no conversion
  }

  /**
   * Calculate cart subtotal excluding gift cards
   * Uses final price AFTER DISCOUNTS converted to SHOP CURRENCY (GBP)
   */
  function calculateSubtotalWithoutGiftCards(cart) {
    let subtotalInPresentmentCurrency = 0;
    
    cart.items.forEach(item => {
      // Exclude gift card items from subtotal calculation
      if (!GIFT_CARD_CONFIG.allVariantIds.includes(item.variant_id.toString())) {
        // Use final_line_price (after all discounts) or final_price * quantity
        const finalPrice = item.final_line_price || (item.final_price * item.quantity) || item.line_price;
        subtotalInPresentmentCurrency += finalPrice;
      }
    });

    // Convert from cents to currency units
    let subtotal = subtotalInPresentmentCurrency / 100;

    // Convert to shop currency (GBP) if needed
    const presentmentCurrency = cart.currency || 'GBP';
    if (presentmentCurrency !== 'GBP') {
      const exchangeRate = getExchangeRateToGBP(presentmentCurrency);
      subtotal = subtotal * exchangeRate;
    }

    return subtotal;
  }

  /**
   * Determine which gift card tier the customer qualifies for
   */
  function getQualifiedGiftCard(subtotal) {
    for (let tier of GIFT_CARD_CONFIG.tiers) {
      if (subtotal >= tier.threshold) {
        return tier;
      }
    }
    return null;
  }

  /**
   * Check if a gift card variant is already in cart
   */
  function findGiftCardInCart(cart) {
    return cart.items.find(item => 
      GIFT_CARD_CONFIG.allVariantIds.includes(item.variant_id.toString())
    );
  }

  /**
   * Remove all gift cards from cart
   */
  async function removeAllGiftCards(cart) {
    const updates = {};
    
    cart.items.forEach(item => {
      if (GIFT_CARD_CONFIG.allVariantIds.includes(item.variant_id.toString())) {
        updates[item.variant_id] = 0;
      }
    });

    if (Object.keys(updates).length > 0) {
      try {
        await fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ updates })
        });
        return true;
      } catch (error) {
        return false;
      }
    }
    return true;
  }

  /**
   * Add gift card to cart
   */
  async function addGiftCard(variantId) {
    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: variantId,
          quantity: 1,
          properties: {
            '_gift_card_promo': 'true',
            '_auto_added': 'threshold_promotion'
          }
        })
      });
      return await response.json();
    } catch (error) {
      return null;
    }
  }

  /**
   * Update cart drawer/display (if applicable)
   */
  function refreshCartUI() {
    // Multiple refresh methods for compatibility with different themes
    
    // 1. Trigger Shopify theme cart refresh
    if (typeof Shopify !== 'undefined' && Shopify.theme && Shopify.theme.cart && typeof Shopify.theme.cart.refresh === 'function') {
      Shopify.theme.cart.refresh();
    }
    
    // 2. Trigger custom cart drawer refresh events
    document.dispatchEvent(new CustomEvent('cart:refresh'));
    document.dispatchEvent(new CustomEvent('cart:updated'));
    
    // 3. For Ajax Cart themes
    if (window.theme && window.theme.cart && typeof window.theme.cart.refresh === 'function') {
      window.theme.cart.refresh();
    }
    
    // 4. Trigger jQuery events if jQuery is available
    if (typeof jQuery !== 'undefined') {
      jQuery(document).trigger('cart.refresh');
      jQuery(document).trigger('ajaxCart.afterCartLoad');
    }
    
    // 5. Rebuy cart refresh - check if method exists first
    if (window.Rebuy && window.Rebuy.SmartCart && typeof window.Rebuy.SmartCart.fetchCart === 'function') {
      try {
        window.Rebuy.SmartCart.fetchCart();
      } catch (e) {
        // Silently fail
      }
    }
    
    // Note: Removed fetch('/cart.js') to prevent infinite loop
    // The above refresh methods should be sufficient for most themes
  }

  /**
   * Main function to manage gift card based on cart total
   */
  async function manageGiftCard() {
    // Prevent concurrent executions
    if (isProcessing) {
      console.log('[Gift Card] Already processing, skipping...');
      return;
    }
    
    isProcessing = true;
    
    try {
      const cart = await getCart();
      if (!cart) return;

      const subtotal = calculateSubtotalWithoutGiftCards(cart);
      const qualifiedTier = getQualifiedGiftCard(subtotal);
      const currentGiftCard = findGiftCardInCart(cart);

      console.log(`[Gift Card] Subtotal: £${subtotal.toFixed(2)} | Tier: ${qualifiedTier ? qualifiedTier.title : 'None'}`);

      // Case 1: Customer qualifies for a gift card
      if (qualifiedTier) {
        // Check if correct gift card is already in cart with correct quantity
        if (currentGiftCard && currentGiftCard.variant_id.toString() === qualifiedTier.variantId) {
          // If quantity is not exactly 1, fix it
          if (currentGiftCard.quantity !== 1) {
            console.log(`[Gift Card] Fixing quantity from ${currentGiftCard.quantity} to 1`);
            await fetch('/cart/change.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                id: currentGiftCard.key,
                quantity: 1
              })
            });
            refreshCartUI();
          } else {
            console.log(`[Gift Card] Correct ${qualifiedTier.title} already in cart`);
            // Only refresh on initial page load, not on every check
            if (!hasInitialCheckCompleted) {
              refreshCartUI();
              hasInitialCheckCompleted = true;
            }
          }
          return;
        }

        // Remove any existing gift cards first
        await removeAllGiftCards(cart);
        
        // Small delay to ensure removal completes
        await new Promise(resolve => setTimeout(resolve, 100));

        // Add the qualified gift card
        console.log(`[Gift Card] Adding ${qualifiedTier.title}`);
        await addGiftCard(qualifiedTier.variantId);
        
        // Refresh cart display
        refreshCartUI();
        hasInitialCheckCompleted = true;
      } 
      // Case 2: Customer no longer qualifies (cart total dropped below £75)
      else if (currentGiftCard) {
        console.log('[Gift Card] Removing - threshold no longer met');
        await removeAllGiftCards(cart);
        
        // Refresh cart display
        refreshCartUI();
        hasInitialCheckCompleted = true;
      } else {
        // No gift card needed and none in cart - mark as complete
        hasInitialCheckCompleted = true;
      }
    } finally {
      // Always release the lock
      isProcessing = false;
    }
  }

  /**
   * Debounce function to prevent excessive API calls
   */
  function debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  // Debounced version of manageGiftCard
  const debouncedManageGiftCard = debounce(manageGiftCard, 1000);

  // Flag to prevent concurrent executions
  let isProcessing = false;
  
  // Flag to prevent our own fetches from triggering the script
  let isInternalFetch = false;
  
  // Flag to track if initial page load check is done
  let hasInitialCheckCompleted = false;

  /**
   * Initialize event listeners
   */
  function init() {
    console.log('[Gift Card] System Initialized');
    
    // Listen to cart changes via Ajax API
    document.addEventListener('cart:updated', debouncedManageGiftCard);
    
    // Listen to add to cart events
    document.addEventListener('cart:add', debouncedManageGiftCard);
    
    // Listen to cart item updates
    document.addEventListener('cart:change', debouncedManageGiftCard);

    // Additional event listeners for broader compatibility
    document.addEventListener('ajaxCart.afterCartLoad', debouncedManageGiftCard);
    
    // Listen for Shopify theme events
    if (typeof jQuery !== 'undefined') {
      jQuery(document).on('cart.requestComplete', debouncedManageGiftCard);
    }

    // Intercept Shopify Ajax Cart API calls
    const originalFetch = window.fetch;
    window.fetch = function(...args) {
      const url = args[0];
      
      // Skip if this is our internal fetch
      if (isInternalFetch) {
        return originalFetch.apply(this, args);
      }
      
      // Monitor cart-related API calls (but not cart.js GET requests)
      if (typeof url === 'string' && (
        url.includes('/cart/add.js') || 
        url.includes('/cart/update.js') || 
        url.includes('/cart/change.js')
      )) {
        console.log(`[Gift Card] Detected cart API call: ${url}`);
        return originalFetch.apply(this, args).then(response => {
          // Trigger gift card check after cart update
          debouncedManageGiftCard();
          return response;
        });
      }
      
      return originalFetch.apply(this, args);
    };

    // Also intercept XMLHttpRequest for older implementations
    const originalXHROpen = XMLHttpRequest.prototype.open;
    const originalXHRSend = XMLHttpRequest.prototype.send;
    
    XMLHttpRequest.prototype.open = function(method, url) {
      this._url = url;
      return originalXHROpen.apply(this, arguments);
    };
    
    XMLHttpRequest.prototype.send = function() {
      if (this._url && typeof this._url === 'string' && (
        this._url.includes('/cart/add.js') ||
        this._url.includes('/cart/update.js') ||
        this._url.includes('/cart/change.js')
      )) {
        this.addEventListener('load', function() {
          console.log(`[Gift Card] Detected XHR cart call: ${this._url}`);
          debouncedManageGiftCard();
        });
      }
      return originalXHRSend.apply(this, arguments);
    };

    // Check cart on page load with a slight delay to ensure cart drawer is ready
    setTimeout(() => {
      console.log('[Gift Card] Running initial check...');
      manageGiftCard();
    }, 300);
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
